import { useEffect, useState } from 'react';
import { getToken, onMessage, isSupported, getMessaging } from 'firebase/messaging';
import { app } from '../helpers/firebase';
import { printLogs } from '../utils/Utilities';

const useFirebaseMessaging = () => {
  const [currentToken, setCurrentToken] = useState('');
  const [messageReceived, setMessageReceived] = useState('');
  const [error, setError] = useState(null);

  const requestPermission = async () => {
    try {
      if (typeof window === 'undefined' || !(await isSupported())) {
        printLogs('Firebase Messaging is not supported in this browser.');
        setError('Firebase Messaging is not supported in this browser.');
        return null;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        printLogs('Notification permission not granted.');
        setError('Notification permission not granted.');
        return null;
      }

      const messaging = getMessaging(app);
      
      let registration = null;
      if ('serviceWorker' in navigator) {
        try {
          const firebaseConfigParams = new URLSearchParams({
            apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
            authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
            projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
            storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
            appId: process.env.REACT_APP_FIREBASE_APP_ID
          }).toString();
          
          const swUrl = `/firebase-messaging-sw.js?${firebaseConfigParams}`;

          // Unregister any old/duplicate service workers to prevent multiple push notifications
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (let reg of registrations) {
            if (reg.active && !reg.active.scriptURL.includes(swUrl)) {
              await reg.unregister();
              printLogs('Unregistered old service worker:', reg.active.scriptURL);
            }
          }

          registration = await navigator.serviceWorker.register(swUrl);
          printLogs('Service Worker registered with scope:', registration.scope);
        } catch (swError) {
          console.error('Service Worker registration failed:', swError);
        }
      }

      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_PUBLIC_VAPID_KEY,
        serviceWorkerRegistration: registration
      });

      if (token) {
        printLogs('Current token:', token);
        setCurrentToken(token);
        return token;
      } else {
        printLogs('No registration token available. Request permission to generate one.');
        setError('No registration token available');
        return null;
      }
    } catch (error) {
      if (error?.code === 'messaging/permission-blocked') {
        printLogs('Notification permission was blocked.');
      } else {
        console.error('An error occurred while retrieving token.', error);
      }
      // Ensure we display the error message in UI
      setError(error?.message || 'An error occurred while retrieving token');
      return null;
    }
  };

  useEffect(() => {
    let unsubscribe;

    const setupMessaging = async () => {
      const path = window.location.pathname;
      if (path.includes('/order-payment') || path.includes('/accepted-order')) {
        return;
      }

      if (typeof window !== 'undefined' && await isSupported()) {
        const messaging = getMessaging(app);
        unsubscribe = onMessage(messaging, (payload) => {
          printLogs('Message received: ', payload);
          setMessageReceived(payload);
        });
        if (Notification.permission === 'granted') {
          requestPermission();
        } else if (Notification.permission === 'denied') {
          setError('Notification permission was denied by the user.');
        } else {
          setError('Notification permission is required.');
        }
      } else {
        printLogs('Firebase Messaging is not supported or window is undefined');
        setError('Firebase Messaging is not supported. Ensure you are on HTTPS or localhost.');
      }
    };

    setupMessaging();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return {
    currentToken,
    messageReceived,
    error,
    requestPermission
  };
};

export default useFirebaseMessaging; 